import { useEffect, useState, useCallback } from "react";
import api from "../lib/api";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Switch } from "../components/ui/switch";
import { Badge } from "../components/ui/badge";
import { Skeleton } from "../components/ui/skeleton";
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "../components/ui/table";
import {
    Collapsible,
    CollapsibleContent,
    CollapsibleTrigger,
} from "../components/ui/collapsible";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuLabel,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from "../components/ui/alert-dialog";
import {
    Radio,
    PlayCircle,
    ExternalLink,
    ChevronDown,
    Bug,
    Plus,
    Pencil,
    Trash2,
    MoreHorizontal,
    Upload,
    Activity,
    ShieldAlert,
    GlobeLock,
    GitBranch,
    GitMerge,
    Undo2,
    Sparkles,
} from "lucide-react";
import { formatDateTime, formatNumber } from "../lib/format";
import { toast } from "sonner";
import SourceFormDialog from "../components/SourceFormDialog";
import ManualImportDialog from "../components/ManualImportDialog";

// ---------------------------------------------------------------------------
// Triage status taxonomy (matches backend SourceStatus literal).
// scannable=true means the Scan button is enabled.
// ---------------------------------------------------------------------------
const STATUS_META = {
    live: {
        label: "Live",
        className: "bg-primary text-primary-foreground border-transparent",
        hint: "Real parser is delivering listings.",
        scannable: true,
    },
    enabled: {
        label: "Enabled · awaiting scan",
        className: "bg-accent/15 text-accent border-transparent",
        hint: "Source is on but no scan has run yet.",
        scannable: true,
    },
    idle: {
        label: "Idle",
        className: "bg-muted text-muted-foreground border-transparent",
        hint: "Disabled. Toggle to enable.",
        scannable: false,
        scanBlockedHint: "Enable source first.",
    },
    running: {
        label: "Running",
        className: "bg-accent/25 text-accent border-transparent animate-pulse",
        hint: "Scan in progress.",
        scannable: false,
    },
    parser_missing: {
        label: "Parser missing",
        className: "bg-[hsl(35_90%_50%/0.15)] text-[hsl(35_90%_45%)] border-transparent",
        hint: "Site reachable, but no parser is wired yet.",
        scannable: false,
        scanBlockedHint: "Parser not implemented yet — build a parser before scanning again.",
    },
    selector_failed: {
        label: "Selector failed",
        className: "bg-destructive/15 text-destructive border-transparent",
        hint: "Parser ran but found zero listing cards.",
        scannable: true,
    },
    empty_result: {
        label: "Empty result",
        className: "bg-muted text-foreground border-transparent",
        hint: "Parser succeeded; source legitimately had no listings.",
        scannable: true,
    },
    access_restricted: {
        label: "Access restricted",
        className: "bg-[hsl(280_45%_55%/0.18)] text-[hsl(280_45%_60%)] border-transparent",
        hint: "401/403/429 — site blocks scraping.",
        scannable: false,
        scanBlockedHint: "Official feed or manual import required.",
    },
    dns_error: {
        label: "DNS error",
        className: "bg-muted text-muted-foreground border-transparent",
        hint: "Domain doesn't resolve.",
        scannable: false,
        scanBlockedHint: "Canonical domain needs review.",
    },
    url_changed: {
        label: "URL changed",
        className: "bg-[hsl(15_75%_50%/0.18)] text-[hsl(15_75%_50%)] border-transparent",
        hint: "HTTP 404 / unexpected redirect.",
        scannable: false,
        scanBlockedHint: "Source URL must be updated.",
    },
    manual_review_required: {
        label: "Manual review",
        className: "bg-[hsl(210_45%_55%/0.18)] text-[hsl(210_55%_65%)] border-transparent",
        hint: "Flagged for human inspection before automation.",
        scannable: false,
        scanBlockedHint: "Source flagged for manual review.",
    },
    official_feed_required: {
        label: "Official feed required",
        className: "bg-[hsl(330_45%_55%/0.18)] text-[hsl(330_55%_65%)] border-transparent",
        hint: "Use CSV import or partner feed.",
        scannable: false,
        scanBlockedHint: "Manual / CSV import only.",
    },
    // Legacy fallback aliases
    not_connected: {
        label: "Not connected",
        className: "bg-muted text-muted-foreground border-transparent",
        hint: "Legacy status — re-scan to reclassify.",
        scannable: false,
    },
    success: {
        label: "Success (legacy)",
        className: "bg-primary/15 text-primary border-transparent",
        hint: "Legacy status — re-scan to reclassify.",
        scannable: true,
    },
    no_listings: {
        label: "No listings (legacy)",
        className: "bg-muted text-foreground border-transparent",
        hint: "Legacy status — re-scan to reclassify.",
        scannable: true,
    },
    failed: {
        label: "Failed (legacy)",
        className: "bg-destructive/15 text-destructive border-transparent",
        hint: "Legacy status — re-scan to reclassify.",
        scannable: true,
    },
};

const RECOMMENDATION_META = {
    keep_live: { label: "Keep running", tone: "primary" },
    good_candidate: { label: "Good next scraper", tone: "accent" },
    build_parser_now: { label: "Build parser now", tone: "accent" },
    needs_official_feed: { label: "Needs official feed", tone: "warn" },
    needs_canonical_url: { label: "Needs canonical URL", tone: "warn" },
    update_url: { label: "Update URL", tone: "warn" },
    update_selectors: { label: "Update selectors", tone: "warn" },
    manual_review: { label: "Manual review", tone: "muted" },
    manual_import_only: { label: "Manual import only", tone: "muted" },
    rescan_later: { label: "Re-scan later", tone: "muted" },
    skip_for_now: { label: "Skip for now", tone: "muted" },
    unknown: { label: "—", tone: "muted" },
};

const recommendForSource = (s) => {
    // Mirror backend logic _recommend(); keeps badge in sync without an extra round-trip.
    if (s.triage_locked) {
        if (s.status === "official_feed_required") return { code: "manual_import_only" };
        return { code: "skip_for_now" };
    }
    const bytes = s.last_response_length || 0;
    switch (s.status) {
        case "live":
            return { code: "keep_live" };
        case "parser_missing":
            if (bytes >= 50000) return { code: "good_candidate" };
            if (bytes >= 5000) return { code: "build_parser_now" };
            return { code: "manual_review" };
        case "access_restricted":
            return { code: "needs_official_feed" };
        case "dns_error":
            return { code: "needs_canonical_url" };
        case "url_changed":
            return { code: "update_url" };
        case "selector_failed":
            return { code: "update_selectors" };
        case "empty_result":
            return { code: "rescan_later" };
        case "manual_review_required":
            return { code: "skip_for_now" };
        case "official_feed_required":
            return { code: "manual_import_only" };
        default:
            return { code: "unknown" };
    }
};

const RecommendBadge = ({ source }) => {
    const rec = recommendForSource(source);
    const meta = RECOMMENDATION_META[rec.code] || RECOMMENDATION_META.unknown;
    const cls =
        meta.tone === "primary"
            ? "border-primary/40 text-primary"
            : meta.tone === "accent"
            ? "border-accent/40 text-accent"
            : meta.tone === "warn"
            ? "border-[hsl(35_90%_50%/0.5)] text-[hsl(35_90%_50%)]"
            : "border-muted-foreground/30 text-muted-foreground";
    return (
        <Badge variant="outline" className={`${cls} text-[10px] uppercase tracking-wider`}>
            <Sparkles className="w-2.5 h-2.5 mr-1" />
            {meta.label}
        </Badge>
    );
};

const Sources = () => {
    const [sources, setSources] = useState([]);
    const [logs, setLogs] = useState([]);
    const [health, setHealth] = useState(null);
    const [loading, setLoading] = useState(true);
    const [running, setRunning] = useState(null);
    const [openLogs, setOpenLogs] = useState({});
    const [editingSource, setEditingSource] = useState(null);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [deleteCandidate, setDeleteCandidate] = useState(null);
    const [importOpen, setImportOpen] = useState(false);
    const [mergePreview, setMergePreview] = useState(null);
    const [removedOpen, setRemovedOpen] = useState(false);
    const [removed, setRemoved] = useState([]);

    const load = useCallback(async () => {
        try {
            const [s, l, h] = await Promise.all([
                api.get("/sources"),
                api.get("/scan/logs", { params: { limit: 30 } }),
                api.get("/sources/health"),
            ]);
            setSources(s.data);
            setLogs(l.data);
            setHealth(h.data);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        load();
        const t = setInterval(load, 6000);
        return () => clearInterval(t);
    }, [load]);

    const toggle = async (id, enabled) => {
        await api.post(`/sources/${id}/toggle`, null, { params: { enabled } });
        load();
    };

    const openAdd = () => {
        setEditingSource(null);
        setDialogOpen(true);
    };

    const openEdit = (source) => {
        setEditingSource(source);
        setDialogOpen(true);
    };

    const confirmDelete = async () => {
        if (!deleteCandidate) return;
        try {
            await api.delete(`/sources/${deleteCandidate.id}`);
            toast.success("Source deleted", { description: deleteCandidate.name });
        } catch (e) {
            toast.error("Could not delete source", {
                description: e?.response?.data?.detail || String(e),
            });
        } finally {
            setDeleteCandidate(null);
            load();
        }
    };

    const runOne = async (id) => {
        setRunning(id);
        try {
            await api.post("/scan/run", { source_ids: [id] });
            toast.success("Scan started", { description: `${id} — running in background` });
        } catch {
            toast.error("Could not start scan");
        } finally {
            setRunning(null);
            load();
        }
    };

    const runAll = async () => {
        setRunning("__all__");
        try {
            await api.post("/scan/run", {});
            toast.success("Bulk scan started", {
                description: "Running enabled scannable sources in the background.",
            });
        } catch {
            toast.error("Could not start scan");
        } finally {
            setRunning(null);
            load();
        }
    };

    const markAs = async (sourceId, status) => {
        try {
            await api.post(`/sources/${sourceId}/mark`, null, { params: { status } });
            toast.success(`Marked as ${status.replace(/_/g, " ")}`);
            load();
        } catch (e) {
            toast.error("Could not mark source");
        }
    };

    const unmark = async (sourceId) => {
        try {
            await api.post(`/sources/${sourceId}/unmark`);
            toast.success("Triage flag cleared");
            load();
        } catch {
            toast.error("Could not clear flag");
        }
    };

    const mergeDuplicates = async () => {
        try {
            const { data: preview } = await api.get("/sources/merge-known-domains/preview");
            if (!preview.total_duplicates) {
                toast.success("No duplicates to merge", {
                    description: "All sources are already canonical.",
                });
                return;
            }
            // Show the preview in the confirmation dialog so the user knows
            // exactly what will be merged before executing.
            setMergePreview(preview);
        } catch {
            toast.error("Preview failed");
        }
    };

    const confirmMerge = async () => {
        try {
            const { data } = await api.post("/sources/merge-known-domains");
            const total = data?.total_deleted || 0;
            toast.success(`${total} duplicate source${total === 1 ? "" : "s"} merged`, {
                description: "URLs appended to the canonical scan targets. Listings preserved.",
            });
            setMergePreview(null);
            load();
        } catch {
            toast.error("Merge failed");
        }
    };

    const openRemoved = async () => {
        try {
            const { data } = await api.get("/sources/removed");
            setRemoved(data || []);
            setRemovedOpen(true);
        } catch {
            toast.error("Could not load deleted sources");
        }
    };

    const restoreSource = async (sourceId) => {
        try {
            const { data } = await api.post(`/sources/${sourceId}/restore`);
            toast.success("Source restored", {
                description: data?.restored ? "Available again in the source list." : "Tombstone removed.",
            });
            // refresh both lists
            const { data: refreshed } = await api.get("/sources/removed");
            setRemoved(refreshed || []);
            if (!refreshed?.length) setRemovedOpen(false);
            load();
        } catch {
            toast.error("Restore failed");
        }
    };

    return (
        <div className="space-y-6" data-testid="sources-page">
            <header className="flex items-end justify-between gap-4 flex-wrap">
                <div>
                    <h1 className="font-grotesk text-2xl md:text-3xl font-semibold">
                        Sources & Scraper Triage
                    </h1>
                    <p className="text-sm text-muted-foreground mt-1 max-w-2xl">
                        Honest classification per source. Scan buttons are disabled when
                        the source isn't realistically scannable (parser missing, access
                        restricted, DNS error, URL changed).
                    </p>
                </div>
                <div className="flex gap-2 flex-wrap">
                    <Button variant="outline" onClick={openRemoved} data-testid="restore-deleted-btn">
                        <Undo2 className="w-4 h-4 mr-1.5" /> Restore deleted
                    </Button>
                    <Button variant="outline" onClick={mergeDuplicates} data-testid="merge-duplicates-btn">
                        <GitMerge className="w-4 h-4 mr-1.5" /> Merge duplicates
                    </Button>
                    <Button variant="outline" onClick={() => setImportOpen(true)} data-testid="manual-import-btn">
                        <Upload className="w-4 h-4 mr-1.5" /> Manual import
                    </Button>
                    <Button variant="outline" onClick={openAdd} data-testid="add-source-btn">
                        <Plus className="w-4 h-4 mr-1.5" /> Add source
                    </Button>
                    <Button onClick={runAll} disabled={running === "__all__"} data-testid="run-all-scan">
                        <PlayCircle className="w-4 h-4 mr-1.5" />
                        {running === "__all__" ? "Starting…" : "Run all enabled"}
                    </Button>
                </div>
            </header>

            <HealthDashboard health={health} loading={loading} />

            {loading ? (
                <Skeleton className="h-80 w-full" />
            ) : (
                <Card className="overflow-hidden">
                    <Table className="table-dense">
                        <TableHeader>
                            <TableRow>
                                <TableHead>Source</TableHead>
                                <TableHead>Triage</TableHead>
                                <TableHead>Recommendation</TableHead>
                                <TableHead>Last probe</TableHead>
                                <TableHead className="text-right">Listings</TableHead>
                                <TableHead>Enabled</TableHead>
                                <TableHead></TableHead>
                                <TableHead></TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {sources.map((s) => {
                                const meta = STATUS_META[s.status] || STATUS_META.idle;
                                return (
                                    <TableRow key={s.id} data-testid={`source-row-${s.id}`}>
                                        <TableCell>
                                            <div className="flex items-center gap-2 font-medium">
                                                <Radio className="w-3.5 h-3.5 text-muted-foreground" />
                                                {s.name}
                                            </div>
                                            <a
                                                href={s.base_url}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="text-xs text-muted-foreground hover:underline flex items-center gap-1"
                                            >
                                                <ExternalLink className="w-3 h-3" /> {s.base_url}
                                            </a>
                                            <div className="flex gap-1.5 items-center mt-1">
                                                <Badge variant="outline" className="text-[9px] capitalize">
                                                    {s.category}
                                                </Badge>
                                                {s.is_user_added ? (
                                                    <Badge variant="outline" className="text-[9px] text-muted-foreground">
                                                        user-added
                                                    </Badge>
                                                ) : (
                                                    <Badge variant="outline" className="text-[9px] text-muted-foreground">
                                                        registry
                                                    </Badge>
                                                )}
                                                {s.has_real_parser && (
                                                    <Badge variant="outline" className="text-[9px] text-primary border-primary/40">
                                                        real parser
                                                    </Badge>
                                                )}
                                            </div>
                                            {Array.isArray(s.extra_urls) && s.extra_urls.length > 0 && (
                                                <ScanTargetsList source={s} />
                                            )}
                                        </TableCell>
                                        <TableCell>
                                            <Badge
                                                data-testid={`source-status-${s.id}`}
                                                className={`${meta.className} text-[10px] uppercase tracking-wider`}
                                            >
                                                {meta.label}
                                            </Badge>
                                            <div className="text-[11px] text-muted-foreground mt-1 leading-snug max-w-[260px]">
                                                {meta.hint}
                                            </div>
                                            {s.last_error && (
                                                <div className="text-[11px] text-destructive/80 mt-1 max-w-[280px] break-words">
                                                    {s.last_error}
                                                </div>
                                            )}
                                            {(s.last_http_status || s.last_response_length) && (
                                                <div className="text-[10px] text-muted-foreground mt-1 flex gap-2 uppercase tracking-wider">
                                                    {s.last_http_status && (
                                                        <span data-testid={`http-status-${s.id}`}>HTTP {s.last_http_status}</span>
                                                    )}
                                                    {s.last_response_length && (
                                                        <span>· {formatNumber(s.last_response_length)} bytes</span>
                                                    )}
                                                </div>
                                            )}
                                        </TableCell>
                                        <TableCell>
                                            <RecommendBadge source={s} />
                                        </TableCell>
                                        <TableCell className="text-xs text-muted-foreground">
                                            {formatDateTime(s.last_run_at)}
                                        </TableCell>
                                        <TableCell className="text-right tabular-nums">
                                            {s.total_listings_found}
                                        </TableCell>
                                        <TableCell>
                                            <Switch
                                                checked={s.enabled}
                                                onCheckedChange={(v) => toggle(s.id, v)}
                                                data-testid={`toggle-${s.id}`}
                                            />
                                        </TableCell>
                                        <TableCell>
                                            {meta.scannable ? (
                                                <Button
                                                    variant="ghost"
                                                    size="sm"
                                                    disabled={running === s.id}
                                                    onClick={() => runOne(s.id)}
                                                    data-testid={`run-${s.id}`}
                                                >
                                                    <PlayCircle className="w-3.5 h-3.5 mr-1" />
                                                    {running === s.id ? "…" : "Scan"}
                                                </Button>
                                            ) : (
                                                <div
                                                    data-testid={`scan-blocked-${s.id}`}
                                                    className="text-[10px] text-muted-foreground italic max-w-[140px] leading-snug"
                                                >
                                                    {meta.scanBlockedHint || "Not scannable"}
                                                </div>
                                            )}
                                        </TableCell>
                                        <TableCell>
                                            <div className="flex gap-1 items-center">
                                                <Button
                                                    variant="ghost"
                                                    size="icon"
                                                    onClick={() => openEdit(s)}
                                                    data-testid={`edit-${s.id}`}
                                                    aria-label="Edit"
                                                    className="w-7 h-7"
                                                >
                                                    <Pencil className="w-3.5 h-3.5" />
                                                </Button>
                                                <DropdownMenu>
                                                    <DropdownMenuTrigger asChild>
                                                        <Button
                                                            variant="ghost"
                                                            size="icon"
                                                            className="w-7 h-7"
                                                            data-testid={`more-${s.id}`}
                                                            aria-label="More"
                                                        >
                                                            <MoreHorizontal className="w-3.5 h-3.5" />
                                                        </Button>
                                                    </DropdownMenuTrigger>
                                                    <DropdownMenuContent align="end">
                                                        <DropdownMenuLabel>Triage</DropdownMenuLabel>
                                                        <DropdownMenuItem onClick={() => markAs(s.id, "manual_review_required")}>
                                                            Mark · manual review
                                                        </DropdownMenuItem>
                                                        <DropdownMenuItem onClick={() => markAs(s.id, "official_feed_required")}>
                                                            Mark · official feed required
                                                        </DropdownMenuItem>
                                                        {s.triage_locked && (
                                                            <DropdownMenuItem onClick={() => unmark(s.id)}>
                                                                Clear triage flag
                                                            </DropdownMenuItem>
                                                        )}
                                                        <DropdownMenuSeparator />
                                                        <DropdownMenuItem
                                                            onClick={() => setDeleteCandidate(s)}
                                                            className="text-destructive"
                                                            data-testid={`delete-${s.id}`}
                                                        >
                                                            <Trash2 className="w-3.5 h-3.5 mr-2" />
                                                            Delete source
                                                        </DropdownMenuItem>
                                                    </DropdownMenuContent>
                                                </DropdownMenu>
                                            </div>
                                        </TableCell>
                                    </TableRow>
                                );
                            })}
                        </TableBody>
                    </Table>
                </Card>
            )}

            <Card className="p-5">
                <h2 className="font-grotesk font-semibold text-base mb-3">Scan logs · debug</h2>
                {logs.length === 0 ? (
                    <p className="text-xs text-muted-foreground">No scans run yet.</p>
                ) : (
                    <div className="space-y-2">
                        {logs.map((log) => {
                            const meta = STATUS_META[log.status] || STATUS_META.idle;
                            const debug = log.debug || {};
                            const isOpen = !!openLogs[log.id];
                            return (
                                <Collapsible
                                    key={log.id}
                                    open={isOpen}
                                    onOpenChange={(v) => setOpenLogs((prev) => ({ ...prev, [log.id]: v }))}
                                >
                                    <div className="border border-border/70 rounded-md overflow-hidden" data-testid={`scan-log-${log.id}`}>
                                        <CollapsibleTrigger className="w-full text-left px-4 py-3 hover:bg-secondary/40 transition-colors">
                                            <div className="flex items-center gap-3 flex-wrap">
                                                <Badge className={`${meta.className} text-[10px] uppercase tracking-wider`}>
                                                    {meta.label}
                                                </Badge>
                                                <div className="font-grotesk font-medium text-sm">{log.source_name}</div>
                                                <div className="text-xs text-muted-foreground">{formatDateTime(log.started_at)}</div>
                                                <div className="text-xs text-muted-foreground">triggered: {log.triggered_by}</div>
                                                <div className="ml-auto flex items-center gap-3 text-xs">
                                                    <span className="tabular-nums">found {log.listings_found}</span>
                                                    <span className="tabular-nums text-primary">+{log.new_listings}</span>
                                                    {log.changed_listings > 0 && <span className="tabular-nums text-accent">Δ{log.changed_listings}</span>}
                                                    {log.error && <span className="text-destructive">err</span>}
                                                    <ChevronDown className={`w-4 h-4 transition-transform ${isOpen ? "rotate-180" : ""}`} />
                                                </div>
                                            </div>
                                        </CollapsibleTrigger>
                                        <CollapsibleContent>
                                            <div className="px-4 py-3 bg-muted/30 border-t border-border/60 text-xs space-y-4">
                                                <div className="flex items-center gap-2 text-muted-foreground uppercase tracking-wider text-[10px] font-medium">
                                                    <Bug className="w-3 h-3" /> Diagnostics
                                                </div>
                                                <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                                                    <DbgRow label="requested_url" value={debug.requested_url} mono link />
                                                    {debug.final_url && <DbgRow label="final_url" value={debug.final_url} mono link />}
                                                    <DbgRow label="http_status_code" value={debug.http_status_code} mono />
                                                    <DbgRow label="response_length" value={debug.response_length ? `${formatNumber(debug.response_length)} bytes` : null} mono />
                                                    <DbgRow label="content_type" value={debug.content_type} mono />
                                                    <DbgRow label="parser_version" value={debug.parser_version} mono />
                                                    <DbgRow label="parser_strategy_used" value={debug.parser_strategy_used} mono />
                                                    <DbgRow label="parser_selector_used" value={debug.parser_selector_used} mono />
                                                    <DbgRow label="total_results_text" value={debug.total_results_text} mono />
                                                    <DbgRow label="overview_pages_found" value={debug.overview_pages_found ?? 0} mono />
                                                    <DbgRow label="candidate_links_found" value={debug.candidate_links_found ?? 0} mono />
                                                    <DbgRow label="listing_cards_found" value={debug.listing_cards_found ?? 0} mono />
                                                    <DbgRow label="detail_pages_attempted" value={debug.detail_pages_attempted ?? 0} mono />
                                                    <DbgRow label="detail_pages_scraped" value={debug.detail_pages_scraped ?? 0} mono />
                                                    <DbgRow label="detail_pages_successful" value={debug.detail_pages_successful ?? 0} mono />
                                                    <DbgRow label="valid_listings_found" value={debug.valid_listings_found ?? 0} mono />
                                                    <DbgRow label="partial_listings_found" value={debug.partial_listings_found ?? 0} mono />
                                                    <DbgRow label="rejected_candidates" value={debug.rejected_candidates ?? 0} mono />
                                                    <DbgRow label="duplicate_count" value={debug.duplicate_count ?? 0} mono />
                                                    <DbgRow label="unchanged_count" value={debug.unchanged_count ?? 0} mono />
                                                    <DbgRow label="created_count" value={debug.created_count ?? log.new_listings ?? 0} mono />
                                                    <DbgRow label="updated_count" value={debug.updated_count ?? log.changed_listings ?? 0} mono />
                                                    <DbgRow label="removed_listings" value={log.removed_listings ?? 0} mono />
                                                    <DbgRow label="started_at" value={formatDateTime(log.started_at)} />
                                                    <DbgRow label="finished_at" value={formatDateTime(log.finished_at)} />
                                                </div>
                                                {Array.isArray(debug.first_10_candidate_titles) && debug.first_10_candidate_titles.length > 0 && (
                                                    <DbgList
                                                        label={`First ${debug.first_10_candidate_titles.length} candidate titles`}
                                                        items={debug.first_10_candidate_titles}
                                                        testid={`candidate-titles-${log.id}`}
                                                    />
                                                )}
                                                {Array.isArray(debug.extra_urls) && debug.extra_urls.length > 0 && (
                                                    <DbgList
                                                        label={`Overview URLs probed (${debug.extra_urls.length})`}
                                                        items={debug.extra_urls}
                                                        testid={`extra-urls-${log.id}`}
                                                    />
                                                )}
                                                {Array.isArray(debug.first_10_candidate_links) && debug.first_10_candidate_links.length > 0 && (
                                                    <DbgList
                                                        label={`First ${debug.first_10_candidate_links.length} candidate detail links`}
                                                        items={debug.first_10_candidate_links}
                                                        testid={`candidate-links-${log.id}`}
                                                    />
                                                )}
                                                {Array.isArray(debug.rejection_reasons) && debug.rejection_reasons.length > 0 && (
                                                    <div>
                                                        <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1">
                                                            rejection_reasons (sample)
                                                        </div>
                                                        <pre className="bg-amber-500/10 text-foreground/90 p-3 rounded text-[11px] whitespace-pre-wrap font-mono leading-relaxed" data-testid={`rejection-reasons-${log.id}`}>
                                                            {debug.rejection_reasons.join("\n")}
                                                        </pre>
                                                    </div>
                                                )}
                                                {debug.contains_keywords && Object.keys(debug.contains_keywords).length > 0 && (
                                                    <div className="flex flex-wrap gap-2">
                                                        <div className="text-[10px] uppercase tracking-wider text-muted-foreground w-full">contains_keywords</div>
                                                        {Object.entries(debug.contains_keywords).map(([k, v]) => (
                                                            <Badge
                                                                key={k}
                                                                variant="outline"
                                                                className={`text-[10px] ${v ? "border-primary/40 text-primary" : "text-muted-foreground"}`}
                                                            >
                                                                {k}: {v ? "yes" : "no"}
                                                            </Badge>
                                                        ))}
                                                    </div>
                                                )}
                                                {debug.error_message && (
                                                    <div>
                                                        <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1">
                                                            error_message
                                                        </div>
                                                        <pre className="bg-destructive/10 text-destructive p-3 rounded text-[11px] whitespace-pre-wrap font-mono leading-relaxed" data-testid={`scan-error-${log.id}`}>
                                                            {debug.error_message}
                                                        </pre>
                                                    </div>
                                                )}
                                                {!debug.error_message && log.error && (
                                                    <div>
                                                        <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1">error</div>
                                                        <pre className="bg-muted p-3 rounded text-[11px] whitespace-pre-wrap font-mono leading-relaxed">{log.error}</pre>
                                                    </div>
                                                )}
                                            </div>
                                        </CollapsibleContent>
                                    </div>
                                </Collapsible>
                            );
                        })}
                    </div>
                )}
            </Card>

            <SourceFormDialog
                open={dialogOpen}
                onOpenChange={setDialogOpen}
                source={editingSource}
                onSaved={() => load()}
            />

            <ManualImportDialog
                open={importOpen}
                onOpenChange={setImportOpen}
                onImported={() => load()}
            />

            <AlertDialog open={!!deleteCandidate} onOpenChange={(o) => { if (!o) setDeleteCandidate(null); }}>
                <AlertDialogContent data-testid="delete-confirm-dialog">
                    <AlertDialogHeader>
                        <AlertDialogTitle>
                            Delete {deleteCandidate?.is_user_added ? "" : "registry "}source?
                        </AlertDialogTitle>
                        <AlertDialogDescription asChild>
                            <div className="space-y-2 text-sm">
                                <div>
                                    This removes <strong>{deleteCandidate?.name}</strong> from the
                                    Sources page along with its scan-log history.
                                </div>
                                <div className="bg-muted/50 border border-border/40 rounded px-3 py-2 text-xs space-y-1">
                                    <div>
                                        <strong>Listings collected by this source stay in the database.</strong>
                                        {" "}They keep their existing
                                        <code className="mx-1 text-[10px] bg-background/60 px-1 py-0.5 rounded">
                                            provenance.source_name
                                        </code>
                                        so the deal-finder still shows them.
                                    </div>
                                    {!deleteCandidate?.is_user_added && (
                                        <div className="text-amber-300">
                                            This is a built-in <strong>registry</strong> source.
                                            A tombstone is kept so the backend doesn't recreate it on restart.
                                            Use the <em>Restore deleted</em> menu to bring it back later.
                                        </div>
                                    )}
                                </div>
                            </div>
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel>Cancel</AlertDialogCancel>
                        <AlertDialogAction
                            onClick={confirmDelete}
                            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                            data-testid="delete-confirm-action"
                        >
                            Delete
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>

            <AlertDialog open={!!mergePreview} onOpenChange={(o) => { if (!o) setMergePreview(null); }}>
                <AlertDialogContent className="max-w-2xl" data-testid="merge-preview-dialog">
                    <AlertDialogHeader>
                        <AlertDialogTitle>Merge {mergePreview?.total_duplicates || 0} duplicate source{(mergePreview?.total_duplicates || 0) === 1 ? "" : "s"}?</AlertDialogTitle>
                        <AlertDialogDescription>
                            Review what will happen before executing. Listings are preserved
                            and re-tagged to the canonical source — only the duplicate source
                            rows + their scan logs are removed.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <div className="max-h-80 overflow-y-auto border border-border/40 rounded p-3 space-y-3 text-xs">
                        {(mergePreview?.previews || []).map((p) => (
                            <div key={p.duplicate.id} className="space-y-1.5 pb-2 border-b border-border/20 last:border-0" data-testid={`merge-preview-row-${p.duplicate.id}`}>
                                <div className="flex items-center gap-2">
                                    <Badge variant="outline" className="text-[9px]">{p.duplicate.id}</Badge>
                                    <span className="font-medium">{p.duplicate.name}</span>
                                    <span className="text-muted-foreground">→</span>
                                    <Badge variant="outline" className="text-[9px] border-primary/40 text-primary">{p.canonical.id}</Badge>
                                    <span className="font-medium">{p.canonical.name}</span>
                                </div>
                                {p.urls_to_add.length > 0 && (
                                    <div className="ml-2">
                                        <span className="text-[10px] uppercase tracking-wider text-muted-foreground">URLs to add</span>
                                        <ul className="space-y-0.5">
                                            {p.urls_to_add.map((u) => (
                                                <li key={u} className="font-mono text-[10px] text-emerald-300 break-all">+ {u}</li>
                                            ))}
                                        </ul>
                                    </div>
                                )}
                                {p.urls_skipped.length > 0 && (
                                    <div className="ml-2">
                                        <span className="text-[10px] uppercase tracking-wider text-muted-foreground">URLs skipped</span>
                                        <ul className="space-y-0.5">
                                            {p.urls_skipped.map((s) => (
                                                <li key={s.url} className="font-mono text-[10px] text-muted-foreground break-all">
                                                    × {s.url} <span className="text-[9px] italic">({s.reason})</span>
                                                </li>
                                            ))}
                                        </ul>
                                    </div>
                                )}
                                {p.listings_to_reassign > 0 && (
                                    <div className="text-[10px] text-amber-300 ml-2">
                                        ↺ {p.listings_to_reassign} listing{p.listings_to_reassign === 1 ? "" : "s"} will have provenance.source_name rewritten to "{p.canonical.name}"
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                    <div className="text-xs text-muted-foreground italic">
                        Total: +{mergePreview?.total_urls_to_add || 0} URLs · ↺ {mergePreview?.total_listings_to_reassign || 0} listings re-tagged · − {mergePreview?.total_duplicates || 0} dupe rows
                    </div>
                    <AlertDialogFooter>
                        <AlertDialogCancel data-testid="merge-cancel">Cancel</AlertDialogCancel>
                        <AlertDialogAction
                            onClick={confirmMerge}
                            data-testid="merge-confirm-action"
                        >
                            Merge {mergePreview?.total_duplicates || 0} source{(mergePreview?.total_duplicates || 0) === 1 ? "" : "s"}
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>

            <AlertDialog open={removedOpen} onOpenChange={setRemovedOpen}>
                <AlertDialogContent className="max-w-xl" data-testid="restore-dialog">
                    <AlertDialogHeader>
                        <AlertDialogTitle>Deleted sources</AlertDialogTitle>
                        <AlertDialogDescription>
                            Sources you've removed are kept here as tombstones so the backend doesn't
                            recreate them automatically. Click <em>Restore</em> to bring one back.
                            User-added sources are hard-deleted and don't appear here.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <div className="max-h-80 overflow-y-auto border border-border/40 rounded p-2 space-y-1.5">
                        {removed.length === 0 ? (
                            <div className="text-xs text-muted-foreground italic py-4 text-center">
                                Nothing deleted. Every registry source is currently active.
                            </div>
                        ) : removed.map((r) => (
                            <div
                                key={r.source_id}
                                className="flex items-center justify-between gap-3 p-2 bg-background/40 rounded text-xs"
                                data-testid={`removed-row-${r.source_id}`}
                            >
                                <div className="min-w-0">
                                    <div className="font-medium">{r.name}</div>
                                    <a href={r.base_url} target="_blank" rel="noopener noreferrer" className="font-mono text-[10px] text-muted-foreground hover:text-accent underline break-all">
                                        {r.base_url}
                                    </a>
                                    <div className="text-[10px] text-muted-foreground mt-0.5">
                                        Removed {r.removed_at?.slice(0, 19).replace("T", " ")} ·
                                        {" "}{r.listings_kept_in_db ?? 0} listings preserved
                                    </div>
                                </div>
                                <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => restoreSource(r.source_id)}
                                    data-testid={`restore-${r.source_id}`}
                                >
                                    <Undo2 className="w-3.5 h-3.5 mr-1.5" /> Restore
                                </Button>
                            </div>
                        ))}
                    </div>
                    <AlertDialogFooter>
                        <AlertDialogCancel>Close</AlertDialogCancel>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </div>
    );
};

// ---------------------------------------------------------------------------
// Scraper Health Dashboard — KPI strip above the table
// ---------------------------------------------------------------------------

const HealthDashboard = ({ health, loading }) => {
    if (loading || !health) {
        return <Skeleton className="h-32 w-full" />;
    }
    const tiles = [
        { label: "Connected", value: `${health.connected_sources}/${health.total_sources}`, icon: Activity, accent: "primary", testid: "kpi-connected" },
        { label: "Live", value: health.live, icon: Activity, accent: "primary", testid: "kpi-live" },
        { label: "Live partial", value: health.live_partial, icon: Activity, accent: "warn", testid: "kpi-live-partial" },
        { label: "Parser missing", value: health.parser_missing, icon: GitBranch, accent: "warn", testid: "kpi-parser-missing" },
        { label: "Access restricted", value: health.access_restricted, icon: GlobeLock, accent: "purple", testid: "kpi-access" },
        { label: "DNS error", value: health.dns_error, icon: ShieldAlert, accent: "muted", testid: "kpi-dns" },
        { label: "URL changed", value: health.url_changed, icon: ShieldAlert, accent: "warn", testid: "kpi-url-changed" },
        { label: "Failed", value: (health.failed || 0) + (health.selector_failed || 0), icon: ShieldAlert, accent: "muted", testid: "kpi-failed" },
        { label: "Total listings", value: formatNumber(health.total_listings), icon: Radio, accent: "primary", testid: "kpi-listings" },
        { label: "Excluded outliers", value: formatNumber(health.excluded_outliers ?? 0), icon: ShieldAlert, accent: "warn", testid: "kpi-excluded-outliers" },
    ];
    const perSource = Object.entries(health.listings_per_source || {}).sort((a, b) => b[1] - a[1]);
    return (
        <Card className="p-5" data-testid="health-card">
            <div className="flex items-end justify-between mb-4 flex-wrap gap-3">
                <div>
                    <div className="text-[11px] uppercase tracking-[0.18em] text-muted-foreground">
                        Scraper health
                    </div>
                    <h2 className="font-grotesk font-semibold text-lg mt-1">
                        {health.pct_connected}% of sources connected ·{" "}
                        <span className="text-muted-foreground">{health.connected_sources} of {health.total_sources}</span>
                    </h2>
                    <p className="text-[11px] text-muted-foreground mt-0.5">
                        Connected = real parser + successful scan + ≥1 listing saved.
                    </p>
                </div>
                <div className="text-xs text-muted-foreground space-y-0.5 text-right">
                    <div>Last live scan: {formatDateTime(health.last_successful_scan_at)}</div>
                    <div>Last failure: {formatDateTime(health.last_failed_scan_at)}</div>
                </div>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
                {tiles.map((t) => (
                    <HealthTile key={t.label} {...t} />
                ))}
            </div>
            {perSource.length > 0 && (
                <div className="mt-5 pt-4 border-t border-border/40">
                    <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground mb-2">
                        Listings per source
                    </div>
                    <div className="flex flex-wrap gap-2" data-testid="listings-per-source">
                        {perSource.map(([name, count]) => (
                            <div key={name} className="bg-muted/40 rounded px-2.5 py-1 text-[11px]">
                                <span className="text-foreground/80">{name}</span>
                                <span className="ml-2 font-medium text-accent">{formatNumber(count)}</span>
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </Card>
    );
};

const HealthTile = ({ label, value, icon: Icon, accent, testid }) => {
    const accentCls =
        accent === "primary"
            ? "text-primary"
            : accent === "warn"
            ? "text-[hsl(35_90%_50%)]"
            : accent === "purple"
            ? "text-[hsl(280_45%_60%)]"
            : "text-muted-foreground";
    return (
        <div className="bg-muted/40 rounded-md px-4 py-3" data-testid={testid}>
            <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-[0.16em] text-muted-foreground">
                <Icon className={`w-3 h-3 ${accentCls}`} />
                {label}
            </div>
            <div className={`kpi-value text-3xl mt-1 ${accentCls}`}>{value ?? 0}</div>
        </div>
    );
};

const DbgRow = ({ label, value, mono, link }) => (
    <div>
        <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-0.5">{label}</div>
        <div className={`${mono ? "font-mono" : ""} text-[11px] text-foreground break-words leading-snug`}>
            {value === null || value === undefined || value === "" ? (
                <span className="text-muted-foreground italic">—</span>
            ) : link ? (
                <a href={String(value)} target="_blank" rel="noopener noreferrer" className="hover:text-accent underline">
                    {String(value)}
                </a>
            ) : (
                String(value)
            )}
        </div>
    </div>
);

const DbgList = ({ label, items, testid }) => (
    <div>
        <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1">{label}</div>
        <ul className="bg-background/60 border border-border/40 rounded p-2 max-h-48 overflow-y-auto space-y-0.5" data-testid={testid}>
            {items.map((u, i) => (
                <li key={`${u}-${i}`} className="text-[11px] font-mono break-all">
                    <a href={u} target="_blank" rel="noopener noreferrer" className="text-muted-foreground hover:text-accent underline">
                        {u}
                    </a>
                </li>
            ))}
        </ul>
    </div>
);

const ScanTargetsList = ({ source }) => {
    const [open, setOpen] = useState(false);
    const [stats, setStats] = useState(null);
    const [loadingStats, setLoadingStats] = useState(false);
    const extras = source.extra_urls || [];
    const total = extras.length + 1;

    useEffect(() => {
        if (!open || stats || loadingStats) return;
        setLoadingStats(true);
        api.get(`/sources/${source.id}/target-stats`)
            .then(({ data }) => setStats(data))
            .catch(() => setStats({ targets: [] }))
            .finally(() => setLoadingStats(false));
    }, [open, stats, loadingStats, source.id]);

    return (
        <Collapsible open={open} onOpenChange={setOpen} className="mt-2">
            <CollapsibleTrigger asChild>
                <button
                    type="button"
                    className="text-[10px] uppercase tracking-wider text-accent hover:text-accent/80 inline-flex items-center gap-1"
                    data-testid={`scan-targets-toggle-${source.id}`}
                >
                    <GitBranch className="w-3 h-3" /> {total} scan target{total === 1 ? "" : "s"}
                    <ChevronDown className={`w-3 h-3 transition-transform ${open ? "rotate-180" : ""}`} />
                </button>
            </CollapsibleTrigger>
            <CollapsibleContent>
                <div className="mt-2 border border-border/40 rounded p-2 bg-background/40 max-h-[480px] overflow-y-auto" data-testid={`scan-targets-list-${source.id}`}>
                    {loadingStats && !stats && (
                        <div className="text-[11px] text-muted-foreground italic py-1">Loading target stats…</div>
                    )}
                    {stats?.targets?.length > 0 ? (
                        <table className="w-full text-[11px]">
                            <thead>
                                <tr className="text-[10px] uppercase tracking-wider text-muted-foreground border-b border-border/30">
                                    <th className="text-left py-1.5 pr-1">Target</th>
                                    <th className="text-right py-1.5 pr-1">HTTP</th>
                                    <th className="text-right py-1.5 pr-1">Links</th>
                                    <th className="text-right py-1.5 pr-1">Valid</th>
                                    <th className="text-right py-1.5 pr-1">DB</th>
                                    <th className="text-right py-1.5 pr-1">Unique</th>
                                    <th className="text-right py-1.5 pr-1">Missing $</th>
                                    <th className="text-right py-1.5 pr-1">Missing m²</th>
                                    <th className="text-right py-1.5 pr-1">Outliers</th>
                                    <th className="text-left py-1.5 pr-1">Status</th>
                                </tr>
                            </thead>
                            <tbody>
                                {stats.targets.map((t) => (
                                    <TargetRow key={t.target_url} t={t} />
                                ))}
                            </tbody>
                        </table>
                    ) : (
                        // Fallback: render the URL list when we have no scan history yet.
                        !loadingStats && (
                            <ul className="space-y-1">
                                <li className="text-[11px] flex gap-2 items-start">
                                    <Badge variant="outline" className="text-[9px] mt-0.5">primary</Badge>
                                    <a href={source.base_url} target="_blank" rel="noopener noreferrer" className="font-mono text-muted-foreground hover:text-accent underline break-all">
                                        {source.base_url}
                                    </a>
                                </li>
                                {extras.map((u, i) => (
                                    <li key={`${u}-${i}`} className="text-[11px] flex gap-2 items-start">
                                        <Badge variant="outline" className="text-[9px] mt-0.5">extra</Badge>
                                        <a href={u} target="_blank" rel="noopener noreferrer" className="font-mono text-muted-foreground hover:text-accent underline break-all">{u}</a>
                                    </li>
                                ))}
                            </ul>
                        )
                    )}
                </div>
                <p className="text-[10px] text-muted-foreground italic mt-1">
                    All targets run through the same parser. Counts come from the most recent scan + the live DB.
                </p>
            </CollapsibleContent>
        </Collapsible>
    );
};

const TARGET_STATUS_STYLE = {
    live: "text-emerald-300 border-emerald-500/40 bg-emerald-500/10",
    empty_result: "text-amber-300 border-amber-500/40 bg-amber-500/10",
    selector_failed: "text-rose-300 border-rose-500/40 bg-rose-500/10",
    access_restricted: "text-rose-300 border-rose-500/40 bg-rose-500/10",
    dns_error: "text-rose-300 border-rose-500/40 bg-rose-500/10",
    url_changed: "text-amber-300 border-amber-500/40 bg-amber-500/10",
};

const TargetRow = ({ t }) => {
    const ls = t.last_scan || {};
    const db = t.db_listings || {};
    const status = ls.status || "—";
    return (
        <tr className="border-b border-border/20 hover:bg-muted/10" data-testid={`target-row-${t.target_url.slice(-40)}`}>
            <td className="py-1.5 pr-1">
                <div className="flex items-center gap-1.5">
                    <span className="font-medium">{t.target_name || "—"}</span>
                    {t.is_primary && <Badge variant="outline" className="text-[8px] px-1 py-0">primary</Badge>}
                </div>
                <a href={t.target_url} target="_blank" rel="noopener noreferrer" className="font-mono text-[9px] text-muted-foreground hover:text-accent underline break-all">
                    {(() => { try { return new URL(t.target_url).pathname; } catch { return t.target_url; } })()}
                </a>
            </td>
            <td className="text-right pr-1 tabular-nums">{ls.http_status ?? "—"}</td>
            <td className="text-right pr-1 tabular-nums">{ls.candidate_links_found ?? 0}</td>
            <td className="text-right pr-1 tabular-nums">
                {ls.valid_listings_found ?? 0}
                {ls.partial_listings_found > 0 && (
                    <span className="text-[9px] text-muted-foreground"> +{ls.partial_listings_found}p</span>
                )}
            </td>
            <td className="text-right pr-1 tabular-nums font-medium">{db.total ?? 0}</td>
            <td className="text-right pr-1 tabular-nums">{db.unique_to_target ?? 0}</td>
            <td className="text-right pr-1 tabular-nums">{db.missing_price ?? 0}</td>
            <td className="text-right pr-1 tabular-nums">{db.missing_m2 ?? 0}</td>
            <td className="text-right pr-1 tabular-nums">{db.outliers ?? 0}</td>
            <td className="pr-1">
                <Badge variant="outline" className={`text-[9px] capitalize ${TARGET_STATUS_STYLE[status] || ""}`}>
                    {status.replace(/_/g, " ")}
                </Badge>
            </td>
        </tr>
    );
};

export default Sources;


